import * as R from 'ramda'
import { WORKFLOW_TYPES, PROCESS_TYPES_OPTS } from '@/constants/workflow'
import { sizestr } from '@/utils/utils'
import i18n from '@/locales'
import { getTimeRangeFilter } from '@/utils/common/tableFilter'
import store from '@/store'

export const statusMap = function (pdk) {
  const mp = {
    ACTIVE: {
      text: pdk === WORKFLOW_TYPES.CUSTOMER_SERVICE ? i18n.t('common_439') : i18n.t('common_440'),
      color: '#f6a100',
    },
    COMPLETED: {
      text: i18n.t('common_441'),
      color: '#7ed321',
    },
    EXTERNALLY_TERMINATED: {
      text: i18n.t('common_442'),
      color: '#8a94a6',
    },
    CUSTOM_TODO: {
      text: i18n.t('common_439'),
      color: '#f6a100',
    },
    CUSTOM_DOING: {
      text: i18n.t('common_443'),
      color: '#f6a100',
    },
  }
  return mp
}

// 审批结果
export const auditStatusMap = function (pdk) {
  const mp = {
    approved: {
      text: i18n.t('common_368'),
      color: '#7ed321',
    },
    refused: {
      text: pdk === WORKFLOW_TYPES.CUSTOMER_SERVICE ? i18n.t('common_444') : i18n.t('common_370'),
      color: '#F56C6C',
    },
  }
  return mp
}

// 状态结果
export const approveStatusMap = {
  ServiceTask_Onecloud: {
    true: i18n.t('common_445'),
    false: i18n.t('common_446'),
  },
  ServiceTask_StatusCheck: {
    true: i18n.t('common_447'),
    false: i18n.t('common_448'),
  },
  UserTask_User_Confirm: {
    true: i18n.t('common_449'),
    false: i18n.t('common_450'),
  },
}

/**
 * 与 ProcessHistory.getApproveHandle 一致：该日志行是否属于「审批结果」列中有通过/驳回展示的行（非 '-'）。
 * 客服工单走 state 分支，返回 false，交由 variables.audit_status 等判断。
 */
export function isProcessHistoryApprovalRow (row, processDefinitionKey) {
  if (R.isNil(row.task) || R.isNil(row.task.local_variables)) return false
  if (processDefinitionKey === WORKFLOW_TYPES.CUSTOMER_SERVICE) return false
  const statusObj = approveStatusMap[`${row.activity_id}`]
  const approved = row.task.local_variables.approved
  if (!statusObj) {
    return approved !== undefined
  }
  return true
}

/**
 * 与 ProcessHistory.getApproveHandle 一致：该行在审批结果列是否为「通过」（绿色 pass）。
 * 对应逻辑：有 approveStatusMap 时与无 map 时均以 local_variables.approved 真值判定为通过。
 */
export function isProcessHistoryApprovalPassed (row, processDefinitionKey) {
  if (!isProcessHistoryApprovalRow(row, processDefinitionKey)) return false
  const approved = row.task.local_variables.approved
  return !!approved
}

export const getServerConf = (row) => {
  if (row.variables) {
    return JSON.parse((row.variables['server-create-paramter'] || row.variables.paramter) || '{}')
  }
  return {}
}

export function getIncrementFlag (newVal, oldVal, unit) {
  if (newVal > oldVal) {
    return `+${newVal - oldVal}`
  }
  if (newVal < oldVal) {
    return `-${oldVal - newVal}`
  }
}

export const diff = (compareValue, baseValue, unit = i18n.t('common_61')) => {
  if (!compareValue) compareValue = 0
  if (!baseValue) baseValue = 0
  let sign = '+'
  let diff = 0
  diff = baseValue - compareValue
  if (diff < 0) sign = '-'
  if (unit === 'sizestr') {
    return `${sign}${sizestr(Math.abs(diff), 'M', 1024)}`
  } else {
    return `${sign}${Math.abs(diff)}${unit}`
  }
}

export const CHANGE_TYPES = {
  'change-bandwidth': i18n.t('common.change_bandwidth'),
}
export const getInternalResourceFilters = () => {
  let internalResourceFilters = {
    process_definition_key: {
      label: i18n.t('common_375'),
      dropdown: true,
      filter: true,
      items: [
        ...PROCESS_TYPES_OPTS.filter(v => v.value !== 'apply-internal-resource').map((v) => {
          return {
            label: v.name,
            key: v.value,
          }
        }),
      ],
      formatter: (val) => {
        const value = (R.is(Array, val)) ? val[0] : val
        if (PROCESS_TYPES_OPTS.some(item => item.value === value)) {
          return `process_definition_key.equals(${value})`
        }
        return `paramter.contains("name":"${value}")`
      },
    },
    priority_level: {
      label: i18n.t('system.text_315'),
      filter: true,
      dropdown: true,
      items: [
        { label: 'P1', key: 'P1' },
        { label: 'P2', key: 'P2' },
        { label: 'P3', key: 'P3' },
      ],
      formatter: (val) => {
        return `variables->priority_level.contains(${val})`
      },
    },
    resource_name: {
      label: i18n.t('common_151'),
    },
    resource_project: {
      label: i18n.t('common_310'),
    },
    created_at: getTimeRangeFilter({ field: 'create_time', label: i18n.t('common_374'), utc: false }),
    end_time: getTimeRangeFilter({ field: 'end_time', label: i18n.t('common_387'), utc: false }),
  }

  if (store?.getters.workflow.enableApplyInternalResource) {
    internalResourceFilters = {
      project_name: {
        label: i18n.t('wz_workflow_form.labels.country'),
        filter: true,
        formatter: (val) => {
          return `project_name.contains(${val})`
        },
      },
      process_definition_key: {
        label: i18n.t('common_375'),
        dropdown: true,
        filter: true,
        items: [
          ...PROCESS_TYPES_OPTS.filter(v => v.value !== 'apply-internal-resource').map((v) => {
            return {
              label: v.name,
              key: v.value,
            }
          }),
          { label: i18n.t('system_process_type.1'), key: '云资源申请' },
          { label: i18n.t('system_process_type.11'), key: '云资源变更（升降配）' },
          { label: i18n.t('system_process_type.21'), key: '云资源释放' },
        ],
        formatter: (val) => {
          const value = (R.is(Array, val)) ? val[0] : val
          if (PROCESS_TYPES_OPTS.some(item => item.value === value)) {
            return `process_definition_key.equals(${value})`
          }
          return `paramter.contains("name":"${value}")`
        },
      },
      project: {
        label: i18n.t('wz_workflow_form.labels.project_name'),
        filter: true,
        formatter: (val) => {
          return `paramter.contains(${val})`
        },
      },
      // data_from: {
      //   label: i18n.t('wz_workflow_form.labels.data_from'),
      //   dropdown: true,
      //   filter: true,
      //   items: [
      //     { label: i18n.t('wz_workflow_form.data_from.irs_order'), key: 'irs_order' },
      //     { label: i18n.t('wz_workflow_form.data_from.new_order'), key: 'new_order' },
      //   ],
      //   formatter: (val) => {
      //     return `paramter.contains("id":"${val}")`
      //   },
      // },
      created_at: getTimeRangeFilter({ field: 'create_time', label: i18n.t('common_374'), utc: false }),
      end_time: getTimeRangeFilter({ field: 'end_time', label: i18n.t('common_387'), utc: false }),
    }
  }
  return internalResourceFilters
}
